import { Box, Checkbox, Chip, IconButton, Typography, CircularProgress } from '@mui/material';
import {
  BookmarkBorder,
  Bookmark,
  Edit,
  ErrorOutline,
} from '@mui/icons-material';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { ThumbnailImage } from '../ThumbnailImage';
import { ReusePlaceholder, DeletedPlaceholder } from '../placeholders';
import { formatCreationDateThumbnail } from '../../utils/catalogFormatters';
import { CatalogViewProps } from './types';

const RETIRED_STATUSES = new Set(['CLEARED', 'DELETED']);

interface CatalogGridViewProps extends CatalogViewProps {
  gridColumns: string;
}

/**
 * Grid view for catalog items — card layout with thumbnails, titles, and metadata.
 * Extracted from MyObjects.tsx for view mode switching.
 */
export const CatalogGridView = ({
  items,
  gridColumns,
  isBookmarked,
  toggleBookmark,
  onEdit,
  canEdit,
  browseItemIds,
  browseOwnerIds,
  routePrefix,
  browseTotal,
  browsePageOffset,
  browsePage,
  browseQuery,
  browsePageSize,
  isSelected,
  onToggleSelect,
  selectionMode,
  showInventoryNumber,
  buildItemLink,
}: CatalogGridViewProps) => {
  const theme = useTheme();

  return (
    <>
      {items.map((artworkItem, index) => {
        const artworkId = artworkItem.catalogItemId;
        const thumbnailUrl = artworkItem.coverImageUrl || null;
        const title = artworkItem.status === 'CLEARED' ? 'Available for Reuse' : (artworkItem.title || 'Untitled');
        const isProcessing =
          artworkItem.processingStatus === 'UPLOADING' ||
          artworkItem.processingStatus === 'PROCESSING';
        const isFailed = artworkItem.processingStatus === 'FAILED';
        const isRetired = RETIRED_STATUSES.has(artworkItem.status ?? '');
        // DESTROYED is tracked separately from RETIRED_STATUSES: the real
        // artwork image must still render (destruction is a historical
        // record), but we still want the card dimmed as a visual cue.
        const isDestroyed = artworkItem.status === 'DESTROYED';

        const itemLink = buildItemLink
          ? buildItemLink(artworkItem)
          : { to: `/object-details/${artworkId}`, routePrefix: '/object-details' };

        return (
          <Box
            key={`artwork-${artworkId}`}
            component={Link}
            to={itemLink.to}
            state={browseItemIds ? { itemIds: browseItemIds, currentIndex: index, routePrefix: itemLink.routePrefix || routePrefix || '/object-details', total: browseTotal, pageOffset: browsePageOffset, browsePage, query: browseQuery, pageSize: browsePageSize, itemOwnerIds: browseOwnerIds } : undefined}
            sx={{
              p: { xs: 0.75, sm: 1, md: 1.5 },
              pt: { xs: 4, sm: 4.5, md: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              overflow: 'hidden',
              cursor: 'pointer',
              position: 'relative',
              width: '100%',
              textDecoration: 'none',
              color: 'inherit',
              borderRight: `1px solid ${theme.palette.divider}`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              ...(selectionMode && isSelected?.(artworkId) && {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: -2,
              }),
              ...((isRetired || isDestroyed) && { opacity: 0.6 }),
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
              '&:hover .card-action-icon, &:focus-within .card-action-icon': {
                opacity: 1,
              },
              '@media (hover: none)': {
                '.card-action-icon': { opacity: 1 },
              },
            }}
          >
            {/* Selection checkbox - top left */}
            {canEdit && selectionMode && onToggleSelect && (
              <Box
                className="card-action-icon card-action-checkbox"
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  opacity: isSelected?.(artworkId) ? 1 : 0,
                  transition: 'opacity 150ms ease-in-out',
                }}
              >
                <Checkbox
                  size="small"
                  checked={isSelected?.(artworkId) ?? false}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleSelect(artworkId);
                  }}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '4px',
                    p: 0.25,
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                  }}
                />
              </Box>
            )}

            {/* Bookmark button - top right (consistent with Public Catalog + Search Results) */}
            <IconButton
              size="small"
              className="card-action-icon card-action-bookmark"
              aria-label={`Bookmark ${title}`}
              aria-pressed={isBookmarked(artworkId)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleBookmark(artworkId);
              }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: isBookmarked(artworkId) ? 'primary.main' : 'text.secondary',
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                zIndex: 1,
                opacity: isBookmarked(artworkId) ? 1 : 0,
                transition: 'opacity 150ms ease-in-out',
              }}
            >
              {isBookmarked(artworkId) ? <Bookmark /> : <BookmarkBorder />}
            </IconButton>

            {/* Edit button - bottom right */}
            {canEdit && (
              <Box
                className="card-action-icon card-action-edit"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  zIndex: 2,
                  opacity: 0,
                  transition: 'opacity 150ms ease-in-out',
                }}
              >
                <IconButton
                  size="small"
                  aria-label="edit artwork"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit(artworkId);
                  }}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Box>
            )}

            <Box sx={{
              width: '100%',
              maxWidth: '90%',
              position: 'relative',
              flexShrink: 0,
              height: { xs: 120, sm: 150, md: 180, lg: 200 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {isProcessing ? (
                <Box
                  data-testid="processing-indicator"
                  aria-label="Image processing"
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    gap: 1,
                  }}
                >
                  <CircularProgress size={24} color="primary" />
                  <Typography variant="caption" color="text.secondary">
                    Processing…
                  </Typography>
                </Box>
              ) : isFailed ? (
                <Box
                  data-testid="processing-failed-indicator"
                  aria-label="Image processing failed"
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    gap: 1,
                  }}
                >
                  <ErrorOutline sx={{ fontSize: 24, color: 'error.main' }} />
                  <Typography variant="caption" color="error.main">
                    Processing failed
                  </Typography>
                </Box>
              ) : isRetired ? (
                artworkItem.status === 'CLEARED' ? (
                  <ReusePlaceholder variant="thumbnail" />
                ) : (
                  <DeletedPlaceholder variant="thumbnail" />
                )
              ) : (
                <ThumbnailImage
                  src={thumbnailUrl}
                  alt={title}
                />
              )}
            </Box>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              px: 1,
              mt: 1,
              flexGrow: 1,
            }}>
              {artworkItem.status === 'CLEARED' && <Chip label="Reuse" color="success" size="small" sx={{ mb: 0.5 }} />}
              {artworkItem.status === 'DELETED' && <Chip label="Deleted" color="default" size="small" sx={{ mb: 0.5 }} />}
              {title && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    maxWidth: '100%',
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontStyle: 'italic',
                      fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {title}
                  </Typography>
                  {artworkItem.visibility === 'public' ? (
                    <PublicIcon
                      data-testid="visibility-icon-public"
                      aria-label="Public"
                      sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }}
                    />
                  ) : (
                    <LockIcon
                      data-testid="visibility-icon-private"
                      aria-label="Private"
                      sx={{ fontSize: 14, color: 'error.main', flexShrink: 0 }}
                    />
                  )}
                </Box>
              )}
              {formatCreationDateThumbnail(artworkItem.creationDate) && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mb: 0.5,
                    fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.8rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {formatCreationDateThumbnail(artworkItem.creationDate)}
                </Typography>
              )}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                mb: 0.5,
                mt: 'auto',
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: { xs: '0.5rem', sm: '0.6rem', md: '0.7rem' },
                    color: theme.palette.primary.main,
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {showInventoryNumber && artworkItem.customCatalogNumber
                    ? `Inv. ${artworkItem.customCatalogNumber}`
                    : artworkItem.aliCatalogNumber || ''}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </>
  );
};
